import { defineStore, acceptHMRUpdate } from 'pinia'
import { type Checklist as TChecklistTemplate } from '@/api/client/models/Checklist'
import { DocumentationService } from '@/api/client'
import { useServerQueue } from '@/stores/ServerQueue'
import {
  Checklist,
  type TDehydratedChecklist,
  type TChecklistData,
} from '@/classes/sendables/project/Checklist'
import { DateTime } from 'luxon'
import handleRequest from '@/helpers/handleRequest'
import presentToast from '@/composables/useToasts'
import { i18n } from '@/locales/locales'
import { useConfirm } from '@/composables/useConfirm'

type TState = {
  readonly templates: TChecklistTemplate[]
  readonly checklists: Checklist[]
}
const initialState: TState = {
  templates: [],
  checklists: [],
}

const { t } = i18n

const useChecklistStore = defineStore('checklistStore', {
  state: () => {
    return { ...initialState }
  },
  getters: {},
  actions: {
    getTemplate(id: number): TChecklistTemplate | undefined {
      return this.templates.find((template) => template.checklist_id === id)
    },

    getTemplatesForProject(projectId: number): TChecklistTemplate[] {
      return this.templates.filter((template) =>
        template.project_ids?.includes(projectId),
      )
    },

    getChecklistsByProjectId(id: number): TChecklistData[] {
      return this.checklists
        .filter((c) => c.data.project_id === id && !c.data.finished)
        .map((c) => c.data)
    },

    getChecklistById(id: string): TChecklistData {
      const checklistData = this.checklists.find(
        (c) => c.data.result_id === id,
      )?.data
      if (!checklistData)
        throw new Error(`Unable to find Checklist with id ${id}`)
      return checklistData
    },

    createChecklistFromTemplate(
      templateId: number,
      projectId: number,
      employeeId: number,
    ): TChecklistData {
      const template = this.getTemplate(templateId)
      if (!template) {
        throw new Error('Template not found')
      }
      const checklist = Checklist.createFromTemplate(
        template,
        projectId,
        employeeId,
      )
      this.checklists.push(checklist)
      console.log('Checklist created', checklist)
      return checklist.data
    },

    getChecklistModuleTypes() {
      return Checklist.MODULE_TYPES
    },

    updateChecklist(checklist: TChecklistData) {
      console.log(checklist)
      const index = this.checklists.findIndex(
        (c) => c.data.result_id === checklist.result_id,
      )
      if (index === -1) {
        throw new Error('Checklist not found')
      }
      checklist.updated_at = DateTime.utc().toISO()
      this.checklists[index].data = checklist
    },

    removeChecklist(checklistId: string) {
      this.checklists = this.checklists.filter(
        (c) => c.data.result_id !== checklistId,
      )
    },

    destroyChecklist(checklistId: string) {
      this.checklists = this.checklists.filter((c) => {
        if (c.data.result_id === checklistId) {
          console.log('deleting checklist', c)
          c.cleanup()
          return false
        }
        return true
      })
    },

    async sendChecklist(
      id: string,
      handleProgress?: (completed: number, total: number) => void,
      showProgress?: (title: string) => void,
    ) {
      const checklist = this.checklists.find((c) => c.data.result_id === id)
      if (!checklist) {
        throw new Error('Checklist not found')
      }
      let ignoreProblematicFiles = false
      if (checklist.meta.error_log.length > 1) {
        ignoreProblematicFiles = !!(await useConfirm().confirm({
          header: t('phrases.checklist_transmission_problems_in_the_past'),
          message: t('phrases.checklist_send_with_potential_data_loss'),
          cancelButtonText: t('words.no'),
          acceptButtonText: t('words.yes'),
          destructive: true,
        }))
      }
      showProgress?.(t('phrases.checklist_is_sending'))
      const result = await checklist.send(
        ignoreProblematicFiles,
        handleProgress,
      )
      if (result.error) {
        checklist.meta.error_log.push({
          error_message: result.error.error_message,
          error_code: result.error.error_code,
        })
        void presentToast({
          message: t('phrases.checklist_could_not_be_sent'),
          color: 'warning',
        })
        useServerQueue().add(checklist)
      } else {
        this.removeChecklist(id)
        useServerQueue().remove(checklist)
        void presentToast({
          message: t('phrases.checklist_sent_successfully'),
        })
      }
    },

    async sync() {
      const data = (await handleRequest(DocumentationService.getChecklists()))
        ?.data
      if (!data) return
      this.templates = data || []
      console.log('fetched checklist templates', this.templates)
    },

    hydrate(state: string) {
      this.$state = JSON.parse(state, (key, value) => {
        if (key === 'checklists') {
          return value.map((checklistData: TDehydratedChecklist) =>
            Checklist.fromJSON(checklistData),
          )
        } else {
          return value
        }
      })
    },
  },
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useChecklistStore, import.meta.hot))
}

export { useChecklistStore }
